'use client';

import type React from 'react';
import { useCallback } from 'react';
import i18next from 'i18next';
import type { UseTranslationOptions } from 'react-i18next';
import { initReactI18next, useTranslation as useTranslationOrg } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import type { TFunction, TOptions } from 'i18next';
import { getSearchDeployedEntitiesQueryOptions } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type {
  InterfaceNamespaceEntity,
  InterfaceNamespaceTranslationType,
} from '@unifyapps/defs/types/namespace';
import { useQueries } from '@unifyapps/network/react-query';
import {
  getPlatformInterfaceNamespaceFilter,
  getPlatformInterfaceNsArray,
} from './utils/platformInterface';
import { useLanguageContext } from './context/LanguageProvider';
import { getOptions, languages } from './settings';

export type { TFunction } from 'i18next';

export { Trans } from 'react-i18next';

const isServer = typeof window === 'undefined';

// Initialize i18next with React integration and backend resources
i18next
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      // Dynamically import locale files based on language and namespace
      (language: string, namespace: string) => import(`./locales/${language}/${namespace}.json`),
    ),
  )
  .init({
    ...getOptions(),
    lng: isServer ? undefined : document.documentElement.lang, // Detect language on client side
    preload: isServer ? languages : [],
  })
  .catch((err) => {
    console.error('i18next init', err);
  });

/**
 * Custom hook for translation with server-side support
 * @param ns - Namespace for the translations
 * @param options - Options for the hook
 */
export function useTranslation(ns?: string | string[], options?: UseTranslationOptions<undefined>) {
  const lng = useLanguageContext();

  const platformInterfaceNsArray = getPlatformInterfaceNsArray(ns);
  // Note: We are using useQueries and combining the results to get all the translations, because we want each namespace to have its own queryKey
  //       so that prefetching is useful and namespaces are not again fetched on client.
  //       For eg. if in useTranslation we pass ns = ['ns1', 'ns2'], but while prefetching we do only for 'ns1', then prefetching will be useless because of different queryKey
  //       Check server.ts for prefetching part
  const translations = useQueries({
    queries: platformInterfaceNsArray.map((item) => ({
      ...getSearchDeployedEntitiesQueryOptions(
        UAEntityTypes.InterfaceNamespace,
        getPlatformInterfaceNamespaceFilter(lng, item),
      ),
      staleTime: Infinity,
    })),
    combine: (results) => {
      return results.reduce<Record<string, InterfaceNamespaceTranslationType | undefined>>(
        (acc, curr) => {
          curr.data?.objects?.forEach((item: InterfaceNamespaceEntity) => {
            Object.assign(acc, item.properties.translations);
          });

          return acc;
        },
        {},
      );
    },
  });

  const value = useTranslationOrg(ns); // Original translation hook
  const t: TFunction = value.t;

  // Custom translation function to handle rendering issues.
  // The original useTranslationOrg function has some issues, so we override it.
  // If running on the server, include the language (lng) in the options.
  // on client side, we are assigning in i18n initialization above so we can use it directly here.
  const updatedT: TFunction = useCallback(
    (key: string, _options?: TOptions) => {
      // If we have translations in our platform interface namespace, then give priority to it.
      // As, mostly this will be used to override the labels in different tenants.
      // Otherwise, use "t" function from react-i18next
      if (translations[key]) {
        return translations[key].value;
      }

      if (isServer) {
        return t(key, {
          ..._options,
          lng,
        });
      }
      return t(key, _options);
    },
    [translations, t, lng],
  ) as TFunction;

  return {
    ...value,
    t: updatedT, // Use updated translation function
  };
}

export function useChangeLanguage() {
  const { i18n } = useTranslationOrg();

  const setLanguage = useCallback(
    (lng: string) => {
      try {
        void i18n.changeLanguage(lng);
      } catch (error) {
        console.error('Failed to change language', error);
      }
    },
    [i18n],
  );

  return setLanguage;
}

export function LanguageChangeProvider({ children }: { children: React.ReactNode }) {
  // 'use client';
  // const { i18n } = useTranslationOrg();
  //
  // useKey(
  //   (event) => {
  //     return event.ctrlKey && event.key === 'L';
  //   },
  //   () => {
  //     i18n.changeLanguage(i18n.language === 'en' ? 'de' : 'en').catch((err) => {
  //       console.error('ChangeLanguage', err);
  //     });
  //   },
  // );

  return children;
}

export { i18next };
